/**
 * React Hook for Input Validation
 * 
 * This hook provides easy-to-use input validation functionality for React components.
 * It automatically validates and sanitizes user input in real-time.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  ValidationResult,
  ValidationConfig,
  validateInput,
  validateProjectName,
  validateProjectDescription,
  validateArtifactName,
  validateFindingName,
  validateAnalysisName,
  validateAnalysisDescription,
  validateChatMessage,
  validateBurpSuiteHost,
} from '../utils/inputValidation';

export type ValidationType =
  | 'projectName'
  | 'projectDescription'
  | 'artifactName'
  | 'findingName'
  | 'analysisName'
  | 'analysisDescription'
  | 'chatMessage'
  | 'burpSuiteHost'
  | 'custom';

interface UseInputValidationOptions {
  validationType: ValidationType;
  customConfig?: ValidationConfig;
  validateOnChange?: boolean;
  showErrorsImmediately?: boolean;
}

interface UseInputValidationReturn {
  value: string;
  sanitizedValue: string;
  isValid: boolean;
  errors: string[];
  hasBeenTouched: boolean;
  handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur: () => void;
  setValue: (value: string) => void;
  reset: () => void;
  validate: () => boolean;
  getInputProps: () => {
    value: string;
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onBlur: () => void;
    error: boolean;
    helperText: string;
  };
}

/**
 * Hook for input validation with real-time sanitization
 */
export function useInputValidation(
  initialValue: string = '',
  options: UseInputValidationOptions
): UseInputValidationReturn {
  const {
    validationType,
    customConfig,
    validateOnChange = true,
    showErrorsImmediately = false,
  } = options;

  const [value, setValue] = useState<string>(initialValue);
  const [sanitizedValue, setSanitizedValue] = useState<string>(initialValue);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [hasBeenTouched, setHasBeenTouched] = useState<boolean>(false);

  /**
   * Get the appropriate validator based on validation type
   */
  const getValidator = useCallback(
    (input: string): ValidationResult => {
      switch (validationType) {
        case 'projectName':
          return validateProjectName(input);
        case 'projectDescription':
          return validateProjectDescription(input);
        case 'artifactName':
          return validateArtifactName(input);
        case 'findingName':
          return validateFindingName(input);
        case 'analysisName':
          return validateAnalysisName(input);
        case 'analysisDescription':
          return validateAnalysisDescription(input);
        case 'chatMessage':
          return validateChatMessage(input);
        case 'burpSuiteHost':
          return validateBurpSuiteHost(input);
        case 'custom':
          return validateInput(input, customConfig || {});
        default:
          return validateInput(input);
      }
    },
    [validationType, customConfig]
  );

  /**
   * Validate the current value
   */
  const validate = useCallback((): boolean => {
    const result = getValidator(value);
    setIsValid(result.isValid);
    setErrors(result.errors);
    setSanitizedValue(result.sanitizedValue);
    return result.isValid;
  }, [value, getValidator]);

  /**
   * Handle input change
   */
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = event.target.value;
      setValue(newValue);

      if (validateOnChange || hasBeenTouched) {
        const result = getValidator(newValue);
        setIsValid(result.isValid);
        setErrors(result.errors);
        setSanitizedValue(result.sanitizedValue);
      }
    },
    [validateOnChange, hasBeenTouched, getValidator]
  );

  /**
   * Handle input blur
   */
  const handleBlur = useCallback(() => {
    setHasBeenTouched(true);
    validate();
  }, [validate]);

  /**
   * Set value programmatically
   */
  const setValueProgrammatically = useCallback(
    (newValue: string) => {
      setValue(newValue);
      const result = getValidator(newValue);
      setIsValid(result.isValid);
      setErrors(result.errors);
      setSanitizedValue(result.sanitizedValue);
    },
    [getValidator]
  );

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    setValue(initialValue);
    setSanitizedValue(initialValue);
    setIsValid(true);
    setErrors([]);
    setHasBeenTouched(false);
  }, [initialValue]);

  /**
   * Get props to spread on input components
   */
  const getInputProps = useCallback(() => {
    const shouldShowErrors = hasBeenTouched || showErrorsImmediately;
    return {
      value,
      onChange: handleChange,
      onBlur: handleBlur,
      error: shouldShowErrors && !isValid,
      helperText: shouldShowErrors && errors.length > 0 ? errors[0] : '',
    };
  }, [value, handleChange, handleBlur, isValid, errors, hasBeenTouched, showErrorsImmediately]);

  // Validate on mount if showErrorsImmediately is true
  useEffect(() => {
    if (showErrorsImmediately) {
      validate();
    }
  }, [showErrorsImmediately, validate]);

  return {
    value,
    sanitizedValue,
    isValid,
    errors,
    hasBeenTouched,
    handleChange,
    handleBlur,
    setValue: setValueProgrammatically,
    reset,
    validate,
    getInputProps,
  };
}

// Helper function to validate based on type (moved outside to avoid hoisting issues)
function validateFieldByType(
  type: ValidationType,
  value: string,
  customConfig?: ValidationConfig
): ValidationResult {
  switch (type) {
    case 'projectName':
      return validateProjectName(value);
    case 'projectDescription':
      return validateProjectDescription(value);
    case 'artifactName':
      return validateArtifactName(value);
    case 'findingName':
      return validateFindingName(value);
    case 'analysisName':
      return validateAnalysisName(value);
    case 'analysisDescription':
      return validateAnalysisDescription(value);
    case 'chatMessage':
      return validateChatMessage(value);
    case 'burpSuiteHost':
      return validateBurpSuiteHost(value);
    case 'custom':
      return customConfig ? validateInput(value, customConfig) : { sanitizedValue: value, isValid: true, errors: [] };
    default:
      return { sanitizedValue: value, isValid: true, errors: [] };
  }
}

// Helper function to get maxLength for each validation type
function getMaxLengthForType(type: ValidationType, customConfig?: ValidationConfig): number | undefined {
  if (customConfig?.maxLength) return customConfig.maxLength;
  
  switch (type) {
    case 'projectName':
    case 'artifactName':
    case 'findingName':
    case 'analysisName':
      return 100;
    case 'projectDescription':
    case 'analysisDescription':
      return 500;
    case 'chatMessage':
      return 2000;
    case 'burpSuiteHost':
      return 255;
    default:
      return undefined;
  }
}

/**
 * Hook for multiple input fields validation
 * Note: This hook creates validation states for each field without using hooks conditionally
 */
export function useFormValidation(
  fields: Record<string, { validationType: ValidationType; initialValue?: string; customConfig?: ValidationConfig }>
) {
  const [formValid, setFormValid] = useState<boolean>(false);
  const [fieldStates, setFieldStates] = useState<Record<string, {
    value: string;
    sanitizedValue: string;
    isValid: boolean;
    errors: string[];
    hasBeenTouched: boolean;
  }>>(() => {
    // Initialize all field states
    const initialStates: Record<string, any> = {};
    Object.entries(fields).forEach(([key, config]) => {
      const initialValue = config.initialValue || '';
      const validationResult = validateFieldByType(config.validationType, initialValue, config.customConfig);
      initialStates[key] = {
        value: initialValue,
        sanitizedValue: validationResult.sanitizedValue,
        isValid: validationResult.isValid,
        errors: validationResult.errors,
        hasBeenTouched: false,
      };
    });
    return initialStates;
  });

  // Update field value and validation
  const updateField = useCallback((fieldName: string, value: string) => {
    const fieldConfig = fields[fieldName];
    if (!fieldConfig) return;

    // Enforce maxLength before validation
    const maxLength = getMaxLengthForType(fieldConfig.validationType, fieldConfig.customConfig);
    const truncatedValue = maxLength && value.length > maxLength ? value.substring(0, maxLength) : value;

    const validationResult = validateFieldByType(fieldConfig.validationType, truncatedValue, fieldConfig.customConfig);
    
    setFieldStates(prev => ({
      ...prev,
      [fieldName]: {
        value: truncatedValue,
        sanitizedValue: validationResult.sanitizedValue,
        isValid: validationResult.isValid,
        errors: validationResult.errors,
        hasBeenTouched: prev[fieldName]?.hasBeenTouched || false,
      },
    }));
  }, [fields]);

  // Mark field as touched
  const touchField = useCallback((fieldName: string) => {
    setFieldStates(prev => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        hasBeenTouched: true,
      },
    }));
  }, []);

  // Create field validation objects
  const fieldValidations = Object.keys(fields).reduce((acc, key) => {
    const state = fieldStates[key];
    if (!state) return acc;

    acc[key] = {
      value: state.value,
      sanitizedValue: state.sanitizedValue,
      isValid: state.isValid,
      errors: state.errors,
      hasBeenTouched: state.hasBeenTouched,
      handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateField(key, event.target.value);
      },
      handleBlur: () => {
        touchField(key);
      },
      setValue: (value: string) => {
        updateField(key, value);
      },
      reset: () => {
        const config = fields[key];
        updateField(key, config.initialValue || '');
        setFieldStates(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            hasBeenTouched: false,
          },
        }));
      },
      validate: () => {
        touchField(key);
        return state.isValid;
      },
      getInputProps: () => {
        const maxLength = getMaxLengthForType(fields[key].validationType, fields[key].customConfig);
        const charCount = state.value.length;
        const showError = state.hasBeenTouched && !state.isValid;
        
        // Generate helper text
        let helperText = '';
        if (showError) {
          helperText = state.errors[0];
        } else if (maxLength) {
          helperText = `${charCount}/${maxLength} characters`;
        }
        
        return {
          value: state.value,
          onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            updateField(key, event.target.value);
          },
          onBlur: () => {
            touchField(key);
          },
          error: showError,
          helperText: helperText,
        };
      },
    };
    return acc;
  }, {} as Record<string, UseInputValidationReturn>);

  // Check if all fields are valid
  useEffect(() => {
    const allValid = Object.values(fieldStates).every((field) => field.isValid);
    setFormValid(allValid);
  }, [fieldStates]);

  /**
   * Validate all fields
   */
  const validateAll = useCallback((): boolean => {
    // Mark all fields as touched
    setFieldStates(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        updated[key] = { ...updated[key], hasBeenTouched: true };
      });
      return updated;
    });

    // Return whether all are valid
    return Object.values(fieldStates).every((field) => field.isValid);
  }, [fieldStates]);

  /**
   * Reset all fields
   */
  const resetAll = useCallback(() => {
    const resetStates: Record<string, any> = {};
    Object.entries(fields).forEach(([key, config]) => {
      const initialValue = config.initialValue || '';
      const validationResult = validateFieldByType(config.validationType, initialValue, config.customConfig);
      resetStates[key] = {
        value: initialValue,
        sanitizedValue: validationResult.sanitizedValue,
        isValid: validationResult.isValid,
        errors: validationResult.errors,
        hasBeenTouched: false,
      };
    });
    setFieldStates(resetStates);
  }, [fields]);

  /**
   * Get sanitized values for all fields
   */
  const getSanitizedValues = useCallback((): Record<string, string> => {
    return Object.entries(fieldStates).reduce((acc, [key, field]) => {
      acc[key] = field.sanitizedValue;
      return acc;
    }, {} as Record<string, string>);
  }, [fieldStates]);

  return {
    fields: fieldValidations,
    formValid,
    validateAll,
    resetAll,
    getSanitizedValues,
  };
}

/**
 * Simple validation hook for quick use cases
 */
export function useValidatedInput(
  initialValue: string = '',
  validationType: ValidationType
) {
  return useInputValidation(initialValue, {
    validationType,
    validateOnChange: true,
    showErrorsImmediately: false,
  });
}
